import React, { useEffect, useMemo, useState } from "react";
import classes from "./UeTable.module.scss";
import Table, { ColumnHeader } from "../../UI/Table/Table";
import useIsMobile from "../../../hooks/useIsMobile";
import EventList from "../EventList/EventList";
import FilterMenu from "../FilterMenu/FilterMenu";
import Spinner from "../../UI/Spinner/Spinner";
import CustomImage from "../../CustomImage";
import useDebounce from "../../../hooks/useDebounce";

const ROWS_PER_PAGE = 20;

const COLUMN_HEADERS: ColumnHeader[] = [
  { label: "Sr. No.", key: "srNo", style: { textAlign: "center" } },
  {
    label: "Event Name",
    key: "eventName",
    style: { width: "200px" },
  },
  { label: "Study Destination", key: "studydestination" },
  { label: "Event Date", key: "date" },
  { label: "Time", key: "time" },
  { label: "City", key: "cities" },
  { label: "Register Link", key: "register", type: "link" },
];

const UeTable = ({
  tableData,
  tableTitle,
  fetchEvents,
  studyDestinationData,
  cities,
}: {
  tableData: any;
  tableTitle: string;
  fetchEvents: any;
  studyDestinationData: any;
  cities: any;
}) => {
  const isMobile = useIsMobile(970);
  const [loading, setIsLoading] = useState(false);
  const [tableDataLoading, setTableDataLoading] = useState(false);
  const [filteredTableData, setFilteredTableData] = useState<any>({});
  const [searchInputValue, setSearchInputValue] = useState("");
  const [activeFilters, setActiveFilters] = useState<any>({});

  const debouncedFetchEvents = useDebounce(async (value: string) => {
    setIsLoading(true);
    const response = await fetchEvents({ searchValue: value });
    setFilteredTableData(response ?? {});
    setIsLoading(false);
  });

  useEffect(() => {
    setFilteredTableData(tableData ?? {});
  }, [tableData]);

  const searchInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
    debouncedFetchEvents(e.target.value ?? "");
  };

  const handlePageChange = async (page: number) => {
    setTableDataLoading(true);
    const response = await fetchEvents({ ...activeFilters, searchValue: searchInputValue, page });
    setFilteredTableData(response ?? {});
    setTableDataLoading(false);
  };

  const { page = 1, limit = ROWS_PER_PAGE, total } = filteredTableData?.meta?.pagination ?? {};

  const tableRows = useMemo(
    () =>
      (filteredTableData?.data ?? []).map((entry: any, index: number) => ({
        ...entry,
        srNo: (page - 1) * limit + index + 1,
      })),
    [filteredTableData, page, limit],
  );

  return (
    <div className={classes["ue-table-container"]}>
      <h2 className={classes["ue-table-title"]}>{tableTitle ?? "All our upcoming events of the year"}</h2>

      <div className={classes["filter-container"]}>
        <FilterMenu
          fetchEvents={fetchEvents}
          setEvents={setFilteredTableData}
          setIsLoading={setIsLoading}
          onFilterChange={setActiveFilters}
          studyDestinationData={studyDestinationData}
          cities={cities}
        />
        {!isMobile && (
          <div className={classes["search-bar"]}>
            <input
              className={classes["search-input"]}
              type="text"
              name="srch-term"
              id="srch-term"
              placeholder="Search Events"
              value={searchInputValue}
              onChange={searchInputChangeHandler}
            />
            <div className={classes["search-icon"]}>
              <CustomImage src="/searchco.svg" alt="search" width={14} height={14} />
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className={classes["loader"]}>
          <Spinner width={48} height={48} />
        </div>
      ) : isMobile ? (
        <EventList tableData={filteredTableData?.data ?? []} />
      ) : (
        <Table
          columnHeaders={COLUMN_HEADERS}
          tableData={tableRows}
          rowsPerPage={ROWS_PER_PAGE}
          onPageChange={handlePageChange}
          currentPage={page}
          totalItems={total ?? filteredTableData?.data?.length}
          loading={tableDataLoading}
        />
      )}
    </div>
  );
};

export default UeTable;
